import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider, CompanyProvider } from "@/lib/theme";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Activate from "@/pages/Activate";
import ForgotPassword from "@/pages/ForgotPassword";
import Dashboard from "@/pages/Dashboard";
import Employees from "@/pages/Employees";
import Departments from "@/pages/Departments";
import Designations from "@/pages/Designations";
import Products from "@/pages/Products";
import Accounts from "@/pages/Accounts";
import Opportunities from "@/pages/Opportunities";
import OpportunityDetail from "@/pages/OpportunityDetail";
import Tasks from "@/pages/Tasks";
import Travel from "@/pages/Travel";
import Expenses from "@/pages/Expenses";
import Approvals from "@/pages/Approvals";
import Notifications from "@/pages/Notifications";
import Forecast from "@/pages/Forecast";
import Audit from "@/pages/Audit";
import Settings from "@/pages/Settings";
import Payroll from "@/pages/Payroll";
import Attendance from "@/pages/Attendance";
import Inventory from "@/pages/Inventory";
import OEMs from "@/pages/OEMs";
import ProjectExpenses from "@/pages/ProjectExpenses";
import Projects from "@/pages/Projects";
import ProjectDashboard from "@/pages/ProjectDashboard";
import ProjectForm from "@/pages/ProjectForm";
import ProjectDetail from "@/pages/ProjectDetail";
import ProjectCalendar from "@/pages/ProjectCalendar";
import ProjectReports from "@/pages/ProjectReports";
import Orders from "@/pages/Orders";
import OrderForm from "@/pages/OrderForm";
import Vendors from "@/pages/Vendors";
import { ORDER_CREATOR_ROLES } from "@/lib/constants";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <Layout>{children}</Layout>;
};

const RoleGate = ({ roles, children }) => {
  const { user } = useAuth();
  if (!user) return null;
  if (!roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <CompanyProvider>
          <AuthProvider>
            <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/activate" element={<Activate />} />
          <Route path="/forgot" element={<ForgotPassword />} />

          <Route path="/" element={<Protected><Dashboard /></Protected>} />
          <Route path="/opportunities" element={<Protected><Opportunities /></Protected>} />
          <Route path="/opportunities/:id" element={<Protected><OpportunityDetail /></Protected>} />
          <Route path="/accounts" element={<Protected><Accounts /></Protected>} />
          <Route path="/orders" element={<Protected><Orders /></Protected>} />
          <Route path="/orders/new" element={<Protected><RoleGate roles={ORDER_CREATOR_ROLES}><OrderForm /></RoleGate></Protected>} />
          <Route path="/orders/:id" element={<Protected><OrderForm /></Protected>} />
          <Route path="/vendors" element={<Protected><Vendors /></Protected>} />
          <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
          <Route path="/products" element={<Protected><Products /></Protected>} />
          <Route path="/travel" element={<Protected><Travel /></Protected>} />
          <Route path="/expenses" element={<Protected><Expenses /></Protected>} />
          <Route path="/approvals" element={<Protected><RoleGate roles={["super_admin", "hr", "department_manager", "sales_manager"]}><Approvals /></RoleGate></Protected>} />
          <Route path="/forecast" element={<Protected><RoleGate roles={["super_admin", "hr"]}><Forecast /></RoleGate></Protected>} />
          <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
          <Route path="/employees" element={<Protected><RoleGate roles={["super_admin", "hr"]}><Employees /></RoleGate></Protected>} />
          <Route path="/departments" element={<Protected><RoleGate roles={["super_admin", "hr"]}><Departments /></RoleGate></Protected>} />
          <Route path="/designations" element={<Protected><RoleGate roles={["super_admin", "hr"]}><Designations /></RoleGate></Protected>} />
          <Route path="/audit" element={<Protected><RoleGate roles={["super_admin"]}><Audit /></RoleGate></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/payroll" element={<Protected><Payroll /></Protected>} />
          <Route path="/payroll/*" element={<Protected><Payroll /></Protected>} />
          <Route path="/attendance" element={<Protected><Attendance /></Protected>} />
          <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
          <Route path="/oems" element={<Protected><RoleGate roles={["super_admin", "hr", "sales_manager", "department_manager"]}><OEMs /></RoleGate></Protected>} />
          <Route path="/project-expenses" element={<Protected><RoleGate roles={["super_admin", "hr", "sales_manager"]}><ProjectExpenses /></RoleGate></Protected>} />
          <Route path="/projects" element={<Protected><Projects /></Protected>} />
          <Route path="/projects/my" element={<Protected><Projects onlyMine /></Protected>} />
          <Route path="/projects/dashboard" element={<Protected><ProjectDashboard /></Protected>} />
          <Route path="/projects/calendar" element={<Protected><ProjectCalendar /></Protected>} />
          <Route path="/projects/reports" element={<Protected><ProjectReports /></Protected>} />
          <Route path="/projects/new" element={<Protected><RoleGate roles={["super_admin", "hr", "finance_manager", "sales_manager", "department_manager"]}><ProjectForm /></RoleGate></Protected>} />
          <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
          <Route path="/projects/:id/edit" element={<Protected><ProjectForm /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
          </AuthProvider>
        </CompanyProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
